import { useMemo } from "react";

import { useDev } from "../providers/dataContext.js";
import { RecentTrades } from "../components/RecentTrades.js";
import { EaStateBanner } from "../components/EaStateBanner.js";
import { EmptyState, ErrorState, Skeleton } from "../components/States.js";

export function Trades() {
  const { isLoading, error, trades, activeAccount, reload } = useDev();
  const summary = useMemo(() => summarizeTrades(trades), [trades]);

  if (error) {
    return (
      <div className="dash">
        <ErrorState title="Couldn't load trades" body={error} cta={{ label: "Try again", onClick: reload }} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="dash">
        <h1 className="dash__title">Trades</h1>
        <section className="panel">
          <div className="panel__body"><Skeleton height="18px" lines={8} /></div>
        </section>
      </div>
    );
  }

  if (!activeAccount) {
    return (
      <div className="dash">
        <EmptyState
          title="No account selected"
          body="Add a broker account to start tracking trades."
          cta={{ label: "Add account", onClick: () => { window.location.href = "/onboarding"; } }}
        />
      </div>
    );
  }

  if (trades.length === 0) {
    return (
      <div className="dash">
        <section className="route-hero">
          <div>
            <div className="dash__eyebrow">{activeAccount.propFirm} · #{activeAccount.brokerLogin}</div>
            <h1 className="dash__title">Trades</h1>
            <p className="route-hero__lead">Trade journal, filters, notes, and execution review.</p>
          </div>
        </section>
        {activeAccount.eaState && (
          <EaStateBanner
            state={activeAccount.eaState}
            reason={activeAccount.eaStateReason}
            since={activeAccount.eaStateUpdatedAt}
          />
        )}
        <EmptyState
          title="No trades yet"
          body="Once your EA is connected and you place trades, they'll appear here with P&L, R-multiples, and duration."
        />
      </div>
    );
  }

  return (
    <div className="dash">
      <section className="route-hero">
        <div>
          <div className="dash__eyebrow">{activeAccount.propFirm} · #{activeAccount.brokerLogin}</div>
          <h1 className="dash__title">Trades</h1>
          <p className="route-hero__lead">Review execution quality, journal mistakes, and symbol behavior.</p>
        </div>
        <div className="route-hero__stats">
          <div className="route-stat">
            <span>Net P&amp;L</span>
            <strong className={summary.netPnl >= 0 ? "tone-profit" : "tone-loss"}>{formatMoney(summary.netPnl, true)}</strong>
          </div>
          <div className="route-stat">
            <span>Win rate</span>
            <strong>{Math.round(summary.winRate * 100)}%</strong>
          </div>
          <div className="route-stat">
            <span>Avg trade</span>
            <strong className={summary.avgPnl >= 0 ? "tone-profit" : "tone-loss"}>{formatMoney(summary.avgPnl, true)}</strong>
          </div>
        </div>
      </section>
      {activeAccount.eaState && (
        <EaStateBanner
          state={activeAccount.eaState}
          reason={activeAccount.eaStateReason}
          since={activeAccount.eaStateUpdatedAt}
        />
      )}
      <section className="panel">
        <RecentTrades trades={trades} />
      </section>
    </div>
  );
}

function summarizeTrades(trades: Array<{ pnl: number }>): { netPnl: number; winRate: number; avgPnl: number } {
  const netPnl = trades.reduce((sum, t) => sum + t.pnl, 0);
  const wins = trades.filter((t) => t.pnl > 0).length;
  return {
    netPnl,
    winRate: trades.length > 0 ? wins / trades.length : 0,
    avgPnl: trades.length > 0 ? netPnl / trades.length : 0,
  };
}

function formatMoney(value: number, signed = false): string {
  const sign = value < 0 ? "-" : signed && value > 0 ? "+" : "";
  return `${sign}$${Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
